import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Room } from '../models/Rooms';
import { roomsSettingsRoute } from '../config/apiRoutes';
import useFetch from '../hooks/useFetch';
import AuthContext from './AuthContext';
import { Outlet } from 'react-router';
import { SelectOption } from '../components/inputs/StandardSelect';

interface RoomProviderProps {
    children?: any;
}

type RoomContextProps = {
    selectedRoomId: number;
    setSelectedRoomId: (roomId: number) => void;
    rooms: Room[];
    setRooms: (rooms: Room[]) => void;
    roomOptions: SelectOption[];
    handleOptionSelected: (option: SelectOption | null) => void;
    selectedRoomOption: SelectOption | null;
}

const RoomContext = createContext<RoomContextProps>({} as any);

export const RoomProvider = (props: RoomProviderProps) => {
    const [selectedRoomId, setSelectedRoomId] = useLocalStorage(-1, "selected_room");
    const { userInfo } = useContext(AuthContext);
    const [rooms, setRooms] = useState<Room[]>([]);
    const caller = useFetch();
    const roomOptions = useMemo(() => {
        return rooms.map(room => {
            return {
                label: room.room_name,
                value: room.room_id.toString()
            }
        });
    }, [rooms]);
    const selectedRoomOption = useMemo(() => {
        const match = roomOptions.find(option => option.value === selectedRoomId.toString());
        return match ? match : null;
    }, [roomOptions, selectedRoomId])

    async function getRooms() {
        const response = await caller.call<undefined, Room[]>("GET", roomsSettingsRoute);
        if (response.ok && response.data) {
            setRooms(response.data);
            if (response.data.length > 0) {
                const ids = response.data.map(room => parseInt(room.room_id));
                if (!ids.includes(selectedRoomId)) setSelectedRoomId(ids[0]);
            }
        }
    }

    useEffect(() => {
        if (userInfo?.selected_restaurant == null) return;
        getRooms();
    }, [userInfo?.selected_restaurant]);

    useEffect(() => {
        if (roomOptions.length === 0) return;
        const isValid = roomOptions.some(opt => parseInt(opt.value) === selectedRoomId);
        if (!isValid) {
            setSelectedRoomId(parseInt(roomOptions[0].value));
        }
    }, [roomOptions]);

    function handleOptionSelected(option: SelectOption | null) {
        if (option == null) return;
        const selectedRoomId = option.value;
        setSelectedRoomId(parseInt(selectedRoomId));
        setRooms(rooms.map(room => {
            if (selectedRoomId === room.room_id) {
                return { ...room, selected: true }
            }
            return { ...room, selected: false };
        }));
    }

    const state = {
        selectedRoomId,
        setSelectedRoomId,
        rooms,
        setRooms,
        roomOptions,
        handleOptionSelected,
        selectedRoomOption
    }

    return (
        <RoomContext.Provider value={state}>
            <Outlet />
        </RoomContext.Provider>
    )
}

export default RoomContext